import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Save } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  return res.json();
};

const StoreAuthorizationForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    channel_id: '',
    store_name: '',
    store_url: '',
    api_token: '',
    consumer_key: '',
    consumer_secret: '',
    access_token: '',
    access_token_secret: '',
    store_code: 'default',
    default_lang: 'en_US',
    currency: '',      // 仅展示，不提交
    weight_unit: '',   // 仅展示，不提交
    length_unit: '',   // 用户选择后才保存
    sku_prefix: '',
    website_id: '',
    website_code: '',
    website_name: '',
    store_group_id: '',
    store_group_code: '',
    store_group_name: '',
    root_category_id: '',
    is_active: 1,
  });

  // ── Load channels ──
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/channel/stores');
        if (res.status === 'success') setChannels(res.data.channels || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // ── Load existing store data ──
  useEffect(() => {
    if (!isEdit) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      try {
        const res = await apiFetch(`/api/channel/stores/${id}`);
        if (res.status === 'success' && res.data) {
          const s = res.data;
          setFormData({
            channel_id: s.channel_id || '',
            store_name: s.store_name || '',
            store_url: s.store_url || '',
            api_token: '***hidden***',
            consumer_key: s.consumer_key || '',
            consumer_secret: '***hidden***',
            access_token: '***hidden***',
            access_token_secret: '***hidden***',
            store_code: s.store_code || 'default',
            default_lang: s.default_lang || 'en_US',
            currency: s.currency || '',
            weight_unit: s.weight_unit || '',
            length_unit: s.length_unit || '',
            sku_prefix: s.sku_prefix || '',
            website_id: s.website_id || '',
            website_code: s.website_code || '',
            website_name: s.website_name || '',
            store_group_id: s.store_group_id || '',
            store_group_code: s.store_group_code || '',
            store_group_name: s.store_group_name || '',
            root_category_id: s.root_category_id || '',
            is_active: s.is_active ?? 1,
          });
        }
      } catch (e) { console.error('Load store failed:', e); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.channel_id || !formData.store_name) {
      notify(t('common.fill_required'), 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // 只提交用户填写的字段，货币/重量单位由后端 M2 自动获取
      const payload = {
        channel_id: formData.channel_id,
        store_name: formData.store_name,
        store_url: formData.store_url,
        api_token: formData.api_token,
        consumer_key: formData.consumer_key,
        consumer_secret: formData.consumer_secret,
        access_token: formData.access_token,
        access_token_secret: formData.access_token_secret,
        store_code: formData.store_code,
        default_lang: formData.default_lang,
        length_unit: formData.length_unit,
        sku_prefix: formData.sku_prefix,
        is_active: formData.is_active,
      };

      const url = isEdit
        ? `/api/channel/stores/${id}/save`
        : '/api/channel/stores/save';

      const res = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (res.status === 'success') {
        notify(isEdit ? t('common.update_success') : t('common.create_success'), 'success');
        navigate('/channel/stores');
      } else {
        notify(res.message || t('common.error'), 'error');
      }
    } catch (err) {
      notify('Save failed: ' + (err.message || t('common.error')), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Title bar ── */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
          {isEdit ? t('common.edit') : t('common.add')} {t('channel.store_authorization')}
        </h1>
        <button onClick={() => navigate('/channel/stores')}
          className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
          <X size={20} />
        </button>
      </div>

      {/* ── Form ── */}
      <form onSubmit={handleSubmit}
        className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">

        {/* ── Channel ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('channel.channel')} <span className="text-red-500">*</span>
          </label>
          <select value={formData.channel_id}
            onChange={e => setFormData(f => ({ ...f, channel_id: e.target.value }))}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200">
            <option value="">-- {t('common.select')} --</option>
            {channels.map(ch => (
              <option key={ch.channel_id} value={ch.channel_id}>{ch.channel_name} ({ch.channel_code})</option>
            ))}
          </select>
        </div>

        {/* ── Store Name ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('channel.store_name')} <span className="text-red-500">*</span>
          </label>
          <input type="text" value={formData.store_name}
            onChange={e => setFormData(f => ({ ...f, store_name: e.target.value }))}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
            placeholder="My Magento Store" />
        </div>

        {/* ── Store URL ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.store_url')}</label>
          <input type="url" value={formData.store_url}
            onChange={e => setFormData(f => ({ ...f, store_url: e.target.value }))}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
            placeholder="https://mystore.com" />
        </div>

        {/* ── Bearer Token (fallback, 仅在 OAuth 未填写时生效) ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('channel.api_token')} <span className="text-xs text-slate-400 font-normal">({t('channel.auth_bearer')})</span>
          </label>
          <input type="password" value={formData.api_token}
            onChange={e => setFormData(f => ({ ...f, api_token: e.target.value }))}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
            placeholder={isEdit ? t('channel.token_hidden') : ''} />
          <p className="text-xs text-slate-400 dark:text-slate-500">{t('channel.api_token_note')}</p>
        </div>

        {/* ── OAuth 1.0a Section ── */}
        <div className="border-t border-dashed border-slate-300 dark:border-slate-600 pt-8 space-y-8">
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300">
            OAuth 1.0a {t('channel.auth_oauth')}
          </h3>
          <p className="text-xs text-slate-400 dark:text-slate-500">{t('channel.oauth_note')}</p>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.consumer_key')}</label>
            <input type="text" value={formData.consumer_key}
              onChange={e => setFormData(f => ({ ...f, consumer_key: e.target.value }))}
              className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200" />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.consumer_secret')}</label>
            <input type="password" value={formData.consumer_secret}
              onChange={e => setFormData(f => ({ ...f, consumer_secret: e.target.value }))}
              className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
              placeholder={isEdit ? t('channel.token_hidden') : ''} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.access_token')}</label>
            <input type="password" value={formData.access_token}
              onChange={e => setFormData(f => ({ ...f, access_token: e.target.value }))}
              className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
              placeholder={isEdit ? t('channel.token_hidden') : ''} />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.access_token_secret')}</label>
            <input type="password" value={formData.access_token_secret}
              onChange={e => setFormData(f => ({ ...f, access_token_secret: e.target.value }))}
              className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
              placeholder={isEdit ? t('channel.token_hidden') : ''} />
          </div>
        </div>

        {/* ── Store Code ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.store_code')}</label>
          <input type="text" value={formData.store_code}
            onChange={e => setFormData(f => ({ ...f, store_code: e.target.value }))}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200" />
          <p className="text-xs text-slate-400 dark:text-slate-500">{t('channel.store_code_hint')}</p>
        </div>

        {/* ── SKU Prefix ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('channel.sku_prefix')}
          </label>
          <input type="text" value={formData.sku_prefix}
            onChange={e => setFormData(f => ({ ...f, sku_prefix: e.target.value }))}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200"
            placeholder="E-" />
          <p className="text-xs text-slate-400 dark:text-slate-500">{t('channel.sku_prefix_hint')}</p>
        </div>

        {/* ── M2 Config Section (auto-fetched, immutable after save) ── */}
        <hr className="border-slate-200 dark:border-slate-700" />
        <div className="space-y-6">
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300">
            {t('channel.m2_config')}
          </h3>

          {/* ── Currency (auto-fetched from M2, display only) ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.currency')}</label>
            <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
              {isEdit && formData.currency ? (
                <span className="text-slate-500 dark:text-slate-400">{formData.currency}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </div>
          </div>

          {/* ── Default Language (auto-fetched from M2, display only) ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.default_lang')}</label>
            <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
              {isEdit && formData.default_lang ? (
                <span className="text-slate-500 dark:text-slate-400">{formData.default_lang}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </div>
          </div>

          {/* ── Weight Unit (auto-fetched from M2, display only) ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('channel.weight_unit')}</label>
            <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
              {isEdit && formData.weight_unit ? (
                <span className="text-slate-500 dark:text-slate-400">{formData.weight_unit}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </div>
          </div>

          {/* ── Website (auto-fetched from M2, display only) ── */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Website ID</label>
              <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
                {isEdit && formData.website_id ? (
                  <span className="text-slate-500 dark:text-slate-400">{formData.website_id}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Website Code</label>
              <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
                {isEdit && formData.website_code ? (
                  <span className="text-slate-500 dark:text-slate-400">{formData.website_code}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Website Name</label>
              <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
                {isEdit && formData.website_name ? (
                  <span className="text-slate-500 dark:text-slate-400">{formData.website_name}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
          </div>

          {/* ── Store Group (auto-fetched from M2, display only) ── */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Store Group ID</label>
              <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
                {isEdit && formData.store_group_id ? (
                  <span className="text-slate-500 dark:text-slate-400">{formData.store_group_id}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Store Group Code</label>
              <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
                {isEdit && formData.store_group_code ? (
                  <span className="text-slate-500 dark:text-slate-400">{formData.store_group_code}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Store Group Name</label>
              <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
                {isEdit && formData.store_group_name ? (
                  <span className="text-slate-500 dark:text-slate-400">{formData.store_group_name}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
          </div>

          {/* ── Root Category ID ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Root Category ID</label>
            <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm">
              {isEdit && formData.root_category_id ? (
                <span className="text-slate-500 dark:text-slate-400">{formData.root_category_id}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </div>
          </div>

          {/* ── Length Unit ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('channel.length_unit')}
            </label>
            <div className="flex gap-2">
              <select value={formData.length_unit}
                onChange={e => setFormData(f => ({ ...f, length_unit: e.target.value }))}
                className="flex-1 p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent dark:text-slate-200">
                <option value="">-- {t('common.select')} --</option>
                <option value="cm">{t('channel.cm')}</option>
                <option value="in">{t('channel.inch')}</option>
              </select>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">{t('channel.length_unit_hint')}</p>
          </div>

          {/* ── M2 Config Note (create mode only) ── */}
          {formData.channel_id == 1 && !isEdit && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-xs text-blue-600 dark:text-blue-400">
              {t('channel.m2_config_note')}
            </div>
          )}
        </div>

        {/* ── Sync Enabled ── */}
        <div className="flex items-center gap-3">
          <input type="checkbox" id="is_active" checked={formData.is_active === 1}
            onChange={e => setFormData(f => ({ ...f, is_active: e.target.checked ? 1 : 0 }))}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
          <label htmlFor="is_active" className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('channel.sync_enabled')}
          </label>
        </div>

        {/* ── Action buttons ── */}
        <div className="flex justify-end gap-3 pt-6 border-t border-slate-200 dark:border-slate-700">
          <button type="button" onClick={() => navigate('/channel/stores')}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={isSubmitting}
            className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              isSubmitting
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}>
            {isSubmitting ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : <Save size={18} />}
            {isSubmitting ? t('common.processing') : (isEdit ? t('common.update') : t('common.add'))}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoreAuthorizationForm;
